import React, { ReactNode, useEffect, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useMutation } from '@tanstack/react-query';
import type { DefaultError } from '@tanstack/query-core';
import axios, {
  SuccessResponse,
  tryRefreshStoredToken,
} from '../../libraries/axios/axios';
import { authTokenState } from '../../atoms/auth/auth.atom';
import {
  clearLocalStorage,
  clearLocalStorageValue,
  getLocalStorageValue,
  LocalStorageKey,
} from '../../utils/localStorage/localStorage';
import { TokenModel } from '../../domain/Auth';
import {
  ProgressFactory,
  ProgressVariant,
} from '../../common/Progress/ProgressFactory';
import { currentUserState } from '../../atoms/user/user.atom';
import { CurrentUser } from '../../domain/User';
import { apiRoutes } from '../../../config/api/apiRoutes';
import { layoutExtendedState } from '../../atoms/layout/layout.atom';
import { UserSettingsLayoutType } from '../../domain/UserSettings';
import { companyState } from '../../atoms/company/company.atom';

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isInitCompleted, setIsInitCompleted] = useState(false);

  const [authStateValue, setAuthStateValue] = useRecoilState(authTokenState);
  const setCompanyStateValue = useSetRecoilState(companyState);
  const [currentUser, setCurrentUserState] = useRecoilState(currentUserState);
  const setLayoutExtendedState = useSetRecoilState(layoutExtendedState);

  const { mutate } = useMutation<SuccessResponse<CurrentUser>, DefaultError>({
    mutationFn: () => axios.get(apiRoutes.users.me),
    onSuccess: (response) => setCurrentUserState(response.data),
    onSettled: () => setIsInitCompleted(true),
  });

  useEffect(() => {
    setLayoutExtendedState(
      currentUser?.userSettings?.layoutType === UserSettingsLayoutType.FULL,
    );
  }, [currentUser]);

  useEffect(() => {
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const requestUrl = error.response?.config?.url ?? '';

        if (status === 401) {
          const logout = () => {
            setAuthStateValue(null);
            clearLocalStorageValue(LocalStorageKey.token);
            clearLocalStorage();
          };

          if (requestUrl.includes(apiRoutes.auth.refreshToken)) {
            // The refresh request itself was rejected — the session is
            // unrecoverable, so log out.
            logout();
          } else {
            // A normal request failed: try to recover with a refresh before
            // logging out (avoids dropping a still-refreshable session).
            const refreshed = await tryRefreshStoredToken();

            if (!refreshed) {
              logout();
            }
          }
        }

        return Promise.reject(error);
      },
    );
  }, []);

  useEffect(() => {
    setAuthStateValue(getLocalStorageValue<TokenModel>(LocalStorageKey.token));
    setCompanyStateValue(getLocalStorageValue<string>(LocalStorageKey.company));
  }, []);

  useEffect(() => {
    if (authStateValue) {
      mutate();

      const intervalId = setInterval(() => {
        mutate();
      }, 5000);

      return () => clearInterval(intervalId);
    } else {
      setIsInitCompleted(true);
    }
  }, [authStateValue]);

  if (!isInitCompleted) {
    return <ProgressFactory variant={ProgressVariant.linear} />;
  }

  return <>{children}</>;
};

export default AuthProvider;
